/** Form primitives shared by the add-car and add-part flows. */

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="field">
      <div className="field-label">
        {label}
        {hint && <span className="field-hint">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

export function Opts({
  options,
  value,
  onChange,
}: {
  options: readonly string[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="opts">
      {options.map((o) => (
        <button type="button" key={o} className={'opt' + (value === o ? ' opt-on' : '')} onClick={() => onChange(o)}>
          {o}
        </button>
      ))}
    </div>
  );
}

export function TrigToggle({
  icon: Icon,
  name,
  sub,
  on,
  onToggle,
  children,
  noInput,
}: {
  icon: LucideIcon;
  name: string;
  sub: string;
  on: boolean;
  onToggle: () => void;
  children?: ReactNode;
  noInput?: boolean;
}) {
  return (
    <div className={'trig-row' + (on ? ' trig-on' : '')}>
      <button
        type="button"
        className={'switch' + (on ? ' switch-on' : '')}
        aria-pressed={on}
        aria-label={name}
        onClick={onToggle}
      >
        <span className="knob" />
      </button>
      <div className="trig-main">
        <div className="trig-name">
          <Icon size={13} /> {name}
        </div>
        <div className="trig-sub">{sub}</div>
      </div>
      {on && !noInput && <div className="trig-input">{children}</div>}
    </div>
  );
}

export function SheetHead({
  title,
  sub,
  onClose,
  onSave,
  saveLabel,
  saveDisabled,
}: {
  title: string;
  sub?: string;
  onClose: () => void;
  /**
   * In the mock this button did nothing but close the sheet — the flows had no
   * API to call. Now it submits, so it takes a real handler and can be disabled
   * while the request is in flight or the form is not yet valid.
   */
  onSave: () => void;
  saveLabel: string;
  saveDisabled?: boolean;
}) {
  return (
    <div className="sheet-head">
      <button className="sheet-cancel" onClick={onClose}>
        Cancel
      </button>
      <div className="sheet-title">
        {title}
        {sub && <span className="sheet-sub">{sub}</span>}
      </div>
      <button className="sheet-save" onClick={onSave} disabled={saveDisabled}>
        {saveLabel}
      </button>
    </div>
  );
}
